import { useState, useRef, useMemo, useEffect } from 'react';
import { Upload, Download, Trash2, Check, FileText, Loader2, Filter, HelpCircle, Wand2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { taskApi, documentApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import { OATemplateGuide } from '@/components/templates/OATemplateGuide';
import type { TemplateItem } from '@/components/TemplatesPage';

const RESPONSE_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'unset', label: 'No Type Set' },
  { value: 'non-final', label: 'Non-Final OA' },
  { value: 'final', label: 'Final OA' },
  { value: 'restriction-election', label: 'Restriction/Election' },
  { value: 'preliminary-amendment', label: 'Preliminary Amendment' },
  { value: 'general-transmittal', label: 'General Transmittal' },
  { value: 'ex-parte-quayle', label: 'Ex Parte Quayle' },
  { value: 'notice-of-allowance', label: 'Notice of Allowance' },
  { value: 'correct-application-papers', label: 'Correct App. Papers' },
] as const;

interface TemplateListPanelProps {
  templates: TemplateItem[];
  templatesLoading: boolean;
  selectedTemplate: TemplateItem | null;
  onSelectTemplate: (template: TemplateItem | null) => void;
  fetchTemplates: () => Promise<void>;
  onOpenWizard: () => void;
}

export function TemplateListPanel({
  templates,
  templatesLoading,
  selectedTemplate,
  onSelectTemplate,
  fetchTemplates,
  onOpenWizard,
}: TemplateListPanelProps) {
  const { addNotification } = useNotificationStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAutoDetect, setIsAutoDetect] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showGuide, setShowGuide] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TemplateItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [templateToRename, setTemplateToRename] = useState<TemplateItem | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Pre-fill rename input when modal opens
  useEffect(() => {
    if (templateToRename) {
      setRenameValue(templateToRename.template_name.split('/').pop()?.replace(/\.docx$/i, '') || '');
    }
  }, [templateToRename]);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      addNotification({ type: 'warning', title: 'No File', message: 'Please select a .docx file to upload.' });
      return;
    }

    try {
      setUploading(true);
      setUploadStatus('Uploading...');

      const response = await taskApi.uploadTemplate(file, isAutoDetect, uploadName || undefined);

      if (response?.job_id) {
        setUploadStatus('Processing template...');

        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          async (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              addNotification({ type: 'success', title: 'Template Uploaded', message: 'Template has been processed successfully.' });
              await fetchTemplates();
            } else {
              addNotification({ type: 'error', title: 'Error', message: `Template processing failed: ${result?.status || 'Unknown error'}` });
            }
            setUploading(false);
            setUploadStatus('');
            setUploadName('');
            if (fileInputRef.current) fileInputRef.current.value = '';
          },
          (error) => {
            console.error('Upload polling error:', error);
            addNotification({ type: 'error', title: 'Error', message: 'Template processing failed.' });
            setUploading(false);
            setUploadStatus('');
          },
          (_attempts, elapsedTime) => {
            setUploadStatus(`Processing... (${Math.floor(elapsedTime / 1000)}s)`);
          }
        );
      } else {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to start template upload.' });
        setUploading(false);
        setUploadStatus('');
      }
    } catch (error) {
      console.error('Upload error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to upload template.' });
      setUploading(false);
      setUploadStatus('');
    }
  };

  const handleDownload = async (template: TemplateItem) => {
    try {
      const response = await taskApi.getTemplate(template.template_id, template.template_name);
      if (!response?.data) throw new Error('Invalid response');
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = template.template_name.split('/').pop() || 'template.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to download template.' });
    }
  };

  // Sort templates newest first by created_date
  const sortedTemplates = useMemo(() => {
    const hasAnyDate = templates.some((t) => t.created_date);
    if (hasAnyDate) {
      return [...templates].sort((a, b) => {
        const dateA = a.created_date ? new Date(a.created_date).getTime() : 0;
        const dateB = b.created_date ? new Date(b.created_date).getTime() : 0;
        return dateB - dateA;
      });
    }
    return [...templates].reverse();
  }, [templates]);

  // Filter by response type
  const filteredTemplates = useMemo(() => {
    if (typeFilter === 'all') return sortedTemplates;
    if (typeFilter === 'unset') return sortedTemplates.filter((t) => !t.response_type);
    return sortedTemplates.filter((t) => t.response_type === typeFilter);
  }, [sortedTemplates, typeFilter]);

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      setDeleting(true);
      await taskApi.deleteTemplate(templateToDelete.template_id);
      if (selectedTemplate?.template_id === templateToDelete.template_id) {
        onSelectTemplate(null);
      }
      addNotification({ type: 'success', title: 'Deleted', message: 'Template deleted successfully.' });
      await fetchTemplates();
    } catch (error) {
      console.error('Delete error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to delete template.' });
    } finally {
      setDeleting(false);
      setTemplateToDelete(null);
    }
  };

  const handleConfirmRename = async () => {
    if (!templateToRename || !renameValue.trim()) return;
    try {
      setRenaming(true);
      await taskApi.renameTemplate(templateToRename.template_id, renameValue.trim());
      addNotification({ type: 'success', title: 'Renamed', message: 'Template renamed successfully.' });
      await fetchTemplates();
    } catch (error) {
      console.error('Rename error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to rename template.' });
    } finally {
      setRenaming(false);
      setTemplateToRename(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200 bg-white/60 space-y-1.5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Templates</h2>
          <button
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-teal-600 transition-colors"
            onClick={() => setShowGuide(true)}
          >
            Guide
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-7 text-xs w-full">
            <Filter className="w-3 h-3 mr-1 text-slate-400 flex-shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[min(60vh,320px)] overflow-y-auto">
            {RESPONSE_TYPE_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Template List */}
      <ScrollArea className="flex-1 min-h-0 [&>[data-slot=scroll-area-viewport]>div]:!block">
        <div className="p-2 space-y-1">
          {templatesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-400">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">
              No templates yet. Upload a .docx file below.
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              No templates match this filter.
            </div>
          ) : (
            filteredTemplates.map((template) => {
              const isSelected = selectedTemplate?.template_id === template.template_id;
              const displayName = template.template_name.split('/').pop() || template.template_name;
              return (
                <div
                  key={template.template_id}
                  className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-all text-sm min-w-0 ${
                    isSelected
                      ? 'bg-teal-50 border border-teal-200 text-teal-800'
                      : 'hover:bg-slate-50 border border-transparent'
                  }`}
                  onClick={() => onSelectTemplate(isSelected ? null : template)}
                >
                  <FileText className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isSelected ? 'text-teal-600' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <span className="block truncate" title={displayName}>{displayName}</span>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {template.template_id === 'default-non-final-oa-response' && (
                        <span className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">
                          Default
                        </span>
                      )}
                      {template.template_id === 'default-final-oa-response' && (
                        <span className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">
                          Default
                        </span>
                      )}
                      {template.template_id === 'default-restriction-election-response' && (
                        <span className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">
                          Default
                        </span>
                      )}
                      {template.template_id === 'default-preliminary-amendment' && (
                        <span className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-medium">
                          Default
                        </span>
                      )}
                      {template.response_type && (
                        <span className="inline-block text-[10px] leading-tight px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 truncate max-w-full">
                          {template.response_type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-teal-600 hover:bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); setTemplateToRename(template); }}
                      title="Rename"
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                      onClick={(e) => { e.stopPropagation(); handleDownload(template); }}
                      title="Download"
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-400 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); setTemplateToDelete(template); }}
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Upload Section */}
      <div className="border-t border-slate-200 p-3 space-y-2 bg-white/60">
        <div className="text-xs font-medium text-slate-600">Upload Template</div>
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-1.5 border-teal-300 text-teal-700 hover:bg-teal-50 hover:text-teal-800"
          onClick={onOpenWizard}
        >
          <Wand2 className="w-3.5 h-3.5" />
          Guided Setup
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setUploadName(file.name.replace(/\.docx$/i, ''));
            else setUploadName('');
          }}
        />
        <input
          type="text"
          value={uploadName}
          onChange={(e) => setUploadName(e.target.value)}
          placeholder="Template name (optional)"
          disabled={uploading}
          className="w-full text-xs px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-400 disabled:opacity-50"
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="auto-detect"
            checked={isAutoDetect}
            onCheckedChange={(checked) => setIsAutoDetect(!!checked)}
            disabled={uploading}
          />
          <label htmlFor="auto-detect" className="text-xs text-slate-600 cursor-pointer">
            Auto-detect placeholders
          </label>
          <button
            className="text-slate-400 hover:text-teal-600 transition-colors"
            onClick={() => setShowGuide(true)}
            title="What is auto-detect?"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </div>
        <Button
          size="sm"
          className="w-full gap-1.5 bg-teal-600 hover:bg-teal-700"
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {uploadStatus}
            </>
          ) : (
            <>
              <Upload className="w-3.5 h-3.5" />
              Upload
            </>
          )}
        </Button>
      </div>

      <OATemplateGuide open={showGuide} onOpenChange={setShowGuide} />

      {/* Delete confirmation */}
      <Dialog open={!!templateToDelete} onOpenChange={(open) => { if (!open) setTemplateToDelete(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete{' '}
            <span className="font-medium text-slate-800">
              {templateToDelete?.template_name.split('/').pop()}
            </span>
            ? This cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setTemplateToDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="gap-1.5"
            >
              {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename modal */}
      <Dialog open={!!templateToRename} onOpenChange={(open) => { if (!open) setTemplateToRename(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Template name</label>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && renameValue.trim()) handleConfirmRename(); }}
              className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
              autoFocus
              disabled={renaming}
            />
            <p className="text-xs text-slate-400">.docx will be added automatically if omitted.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setTemplateToRename(null)}
              disabled={renaming}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRename}
              disabled={renaming || !renameValue.trim()}
              className="gap-1.5 bg-teal-600 hover:bg-teal-700"
            >
              {renaming && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
